// Analytics utility for tracking user interactions

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

export interface GameAnalytics {
  trackButtonClick: (button: string, game: string) => void;
  trackGameStart: (game: string, difficulty?: string) => void;
  trackGameComplete: (game: string, data: GameCompletionData) => void;
  trackGameAction: (game: string, action: string, value?: number) => void;
}

export interface GameCompletionData {
  score?: number;
  time?: number;
  moves?: number;
  mistakes?: number;
  difficulty?: string;
  completed: boolean;
}

class Analytics implements GameAnalytics {
  private isGoogleAnalyticsAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  trackButtonClick(button: string, game: string): void {
    const eventData = {
      game_name: game,
      button_name: button,
      event_label: `${game}_${button}`
    };

    // Send to Google Analytics
    if (this.isGoogleAnalyticsAvailable()) {
      window.gtag('event', 'button_click', {
        event_category: 'game_interaction',
        ...eventData
      });
    }
  }

  trackGameStart(game: string, difficulty?: string): void {
    const eventData = {
      game_name: game,
      difficulty: difficulty || 'default',
      event_label: game
    };

    // Send to Google Analytics
    if (this.isGoogleAnalyticsAvailable()) {
      window.gtag('event', 'game_start', {
        event_category: 'game_flow',
        ...eventData
      });
    }
  }

  trackGameComplete(game: string, data: GameCompletionData): void {
    const eventData = {
      game_name: game,
      completed: data.completed,
      score: data.score || 0,
      time_seconds: data.time || 0,
      moves: data.moves || 0,
      mistakes: data.mistakes || 0,
      difficulty: data.difficulty || 'default',
      event_label: `${game}_${data.completed ? 'won' : 'abandoned'}`
    };

    // Send to Google Analytics
    if (this.isGoogleAnalyticsAvailable()) {
      window.gtag('event', 'game_complete', {
        event_category: 'game_flow',
        ...eventData
      });
    }
  }

  trackGameAction(game: string, action: string, value?: number): void {
    const eventData = {
      game_name: game,
      action_name: action,
      action_value: value || 0,
      event_label: `${game}_${action}`
    };

    // Send to Google Analytics
    if (this.isGoogleAnalyticsAvailable()) {
      window.gtag('event', 'game_action', {
        event_category: 'game_interaction',
        ...eventData
      });
    }
  }

  trackPageView(game: string): void {
    const eventData = {
      game_name: game,
      page_title: `${game} Game`,
      page_location: window.location.href
    };

    // Send to Google Analytics
    if (this.isGoogleAnalyticsAvailable()) {
      window.gtag('event', 'page_view', eventData);
    }
  }

  trackError(game: string, error: string): void {
    const eventData = {
      game_name: game,
      error_description: error,
      description: `${game}: ${error}`,
      fatal: false
    };

    // Send to Google Analytics
    if (this.isGoogleAnalyticsAvailable()) {
      window.gtag('event', 'exception', {
        description: eventData.description,
        fatal: eventData.fatal
      });
    }
  }
}

export const analytics = new Analytics();